import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Clock, Phone, Mail, User, FileText, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isSunday } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM", "06:00 PM", "06:30 PM"
];

const serviceTypes = [
  "Cochlear Implant Consultation",
  "Hearing Evaluation",
  "Vertigo/Balance Issues",
  "Sinus Problems",
  "Snoring/Sleep Apnea",
  "Voice Disorders",
  "General ENT Consultation",
  "Follow-up Visit",
];

export default function BookAppointmentPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    patient_name: "",
    phone: "",
    email: "",
    service_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Get UTM params from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source") || "website";
    const utmMedium = params.get("utm_medium") || "direct";
    const utmCampaign = params.get("utm_campaign") || "";
    
    setFormData(prev => ({
      ...prev,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    if (!formData.patient_name || !formData.phone) {
      toast.error("Please fill in required fields");
      return;
    }

    setLoading(true);

    try {
      const appointmentData = {
        ...formData,
        preferred_date: format(selectedDate, "yyyy-MM-dd"),
        preferred_time: selectedTime,
      };

      await axios.post(`${API}/appointments`, appointmentData);
      
      setSubmitted(true);
      toast.success("Appointment request submitted successfully!");
    } catch (error) {
      console.error("Error booking appointment:", error);
      toast.error("Failed to submit appointment. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  // Disable past dates and Sundays
  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || isSunday(date);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="appointment-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Appointment Request Received!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for booking. Our team will contact you shortly to confirm your appointment.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-left mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-slate-500">Date:</span>
              <span className="font-medium">{selectedDate && format(selectedDate, "MMMM d, yyyy")}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-slate-500">Time:</span>
              <span className="font-medium">{selectedTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Service:</span>
              <span className="font-medium">{formData.service_type || "General Consultation"}</span>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            For urgent queries, please call us at <a href="tel:+919910317286" className="text-sky-600 font-medium">9910317286</a>
          </p>
          <Button onClick={() => setSubmitted(false)} variant="outline" className="w-full">
            Book Another Appointment
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="book-appointment-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-white py-12 md:py-16">
        <div className="container-width text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Book an Appointment
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mx-auto">
              Schedule your consultation with Dr. Sanjay Sachdeva at Greater Kailash clinic.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-8">
              {/* Left: Calendar */}
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="appointment-calendar">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-sky-600" />
                  Select Date
                </h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  className="rounded-md border"
                  fromDate={new Date()}
                  toDate={addDays(new Date(), 30)}
                />

                {/* Time Slots */}
                <div className="mt-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-sky-600" />
                    Select Time
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={`py-2 px-3 text-sm rounded-lg border transition-colors ${
                          selectedTime === time
                            ? 'bg-sky-600 text-white border-sky-600'
                            : 'border-slate-200 hover:border-sky-300 hover:bg-sky-50'
                        }`}
                        data-testid={`time-slot-${time.replace(/[:\s]/g, '')}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Form Fields */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-sky-600" />
                  Your Details
                </h3>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="patient_name">Full Name *</Label>
                    <Input
                      id="patient_name"
                      name="patient_name"
                      value={formData.patient_name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                      className="mt-1"
                      data-testid="input-patient-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      required
                      className="mt-1"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email (Optional)</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      className="mt-1"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service_type">Type of Consultation</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="select-service">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Additional Notes (Optional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Any specific concerns or symptoms you'd like to mention..."
                      className="mt-1"
                      rows={3}
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-amber-600 hover:bg-amber-700 h-12 font-bold"
                    disabled={loading}
                    data-testid="submit-appointment"
                  >
                    {loading ? "Submitting..." : "Book Appointment"}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By booking, you agree to receive a call from our team to confirm your appointment.
                  </p>
                </div>
              </div>
            </form>

            {/* Contact Info */}
            <div className="mt-8 bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Prefer to call?</h3>
              <div className="flex flex-wrap gap-6">
                <a href="tel:+919910317286" className="flex items-center gap-2 text-sky-600 hover:underline">
                  <Phone className="w-5 h-5" />
                  9910317286
                </a>
                <a href="tel:+919810153116" className="flex items-center gap-2 text-sky-600 hover:underline">
                  <Phone className="w-5 h-5" />
                  9810153116
                </a>
                <a href="mailto:entdrsachdeva25@gmail.com" className="flex items-center gap-2 text-sky-600 hover:underline">
                  <Mail className="w-5 h-5" />
                  entdrsachdeva25@gmail.com
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                <strong>Clinic Timing:</strong> Monday to Saturday, 10:00 AM – 7:00 PM
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
